import { createStore } from './createStore';

const DEFAULT_TEMPLATE = {
  name: '',
  content: '',
  placeholdersUsed: '',
  mentions: [],
  attachments: [],
  inlineImages: []
};

const GlobalChatStoreConfig = {
  id: 'GlobalChatStore',
  initialState: {
    tabs: [],
    activeChat: null
  },
  methods: (set, get) => ({
    setTabs: (updates) => {
      set({ tabs: updates });
    },
    setActiveChat: (chatId) => {
      set({ activeChat: chatId });
    },

    addTab: (params) => {
      const { tabs } = get();

      if (!params?.id) {
        throw new Error(`id is not present`);
      }

      let updatedTabs = [...tabs];
      const presentTabIndex = updatedTabs.findIndex(
        (el) => el?.id === params?.id
      );

      let newTab = {
        key: params?.key || params?.id,
        actions: params?.actions,
        components: params?.components,
        helpers: params?.helpers,
        config: params?.config,
        id: params?.id,
        subject: params?.subject,
        module: params?.module || 'TICKET',
        data: params?.data || null,
        ccUsers: params?.ccUsers,
        bccUsers: params?.bccUsers,
        toUsers: params?.toUsers,
        fromUser: params?.from,
        toUsersConfig: params?.toUsersConfig,
        ccUsersConfig: params?.ccUsersConfig,
        bccUsersConfig: params?.bccUsersConfig,
        recipients: params?.recipients,
        recipientsConfig: params?.recipientsConfig,
        conversationScope: params?.conversationScope,
        formatData: params?.formatData,
        defaultTemplate: params?.defaultTemplate || DEFAULT_TEMPLATE,
        template: params?.template || DEFAULT_TEMPLATE,
        details: params?.editorDetails || {},
        mutationAPI: params?.mutationAPI,
        mutationClient: params?.mutationClient,
        callBack: params?.callBack,
        onTemplateChange: params?.onTemplateChange,
        attachmentUploadParams: params?.attachmentUploadParams,
        attachmentUploadUrl: params?.attachmentUploadUrl,
        onClose: params?.onClose,
        primaryAction: params?.primaryAction,
        onCancel: params?.onCancel,
        hideCloseModal: params?.hideCloseModal,
        checkHideCloseModal: params?.checkHideCloseModal,
        onOpen: params?.onOpen,
        onTitleClick: params?.onTitleClick,
        toolbarContent: params?.toolbarContent,
        showWorklog: params?.showWorklog,
        workItem: params?.workItem,
        showAIRephrase: params?.showAIRephrase,
        aiPrompts: params?.aiPrompts,
        type: params?.type || 'CONVERSATION',
        component: params?.component,
        customProps: params?.customProps,
        preventDefaultProps: params?.preventDefaultProps
      };

      if (params?.hasAdditionalProps) {
        newTab = { ...newTab, ...params };
      }

      if (params?.onOpen) {
        params?.onOpen(newTab);
      }

      const ALLOW_ONLY_ONE_TAB = false;
      const ALLOW_BY_TYPE = true;

      if (ALLOW_ONLY_ONE_TAB) {
        updatedTabs = [newTab];
      } else if (ALLOW_BY_TYPE) {
        const isTypePresentIndex = updatedTabs.findIndex(
          (tab) => tab?.type === newTab?.type
        );
        if (isTypePresentIndex > -1) {
          updatedTabs.splice(isTypePresentIndex, 1, newTab);
        } else {
          updatedTabs = [...updatedTabs, newTab];
        }
      } else {
        if (presentTabIndex > -1) {
          updatedTabs.splice(presentTabIndex, 1, newTab);
        } else {
          updatedTabs = [...updatedTabs, newTab];
        }
      }

      set({ tabs: updatedTabs });
      set({ activeChat: newTab?.id });

      if (params?.openCallBack) {
        params?.openCallBack();
      }

      return newTab;
    },

    updateTab: (updatedTab) => {
      const { tabs } = get();
      const updatedTabs = tabs.map((tab) => {
        if (tab?.id && updatedTab?.id && tab?.id === updatedTab?.id) {
          return updatedTab;
        }
        return tab; // Keep reference equality for unchanged tabs
      });
      set({ tabs: updatedTabs });
    },

    updateTabProperty: (tabId, property, propertyData) => {
      const { tabs } = get();
      const updatedTabs = tabs.map((tab) => {
        if (tab?.id && tabId && tab?.id === tabId) {
          // Only create a new object for the tab being updated
          return { ...tab, [property]: propertyData };
        }
        return tab; // Keep reference equality for unchanged tabs
      });
      set({ tabs: updatedTabs });
    },

    removeTab: (chatId, params) => {
      const { tabs } = get();
      const updatedTabs = [...tabs];
      const presentTabIndex = updatedTabs.findIndex((el) => el?.id === chatId);
      const conversationTabData = updatedTabs?.[presentTabIndex];

      if (presentTabIndex > -1) {
        const onClose = conversationTabData?.onClose;
        if (onClose) {
          onClose(conversationTabData, params);
        }
        updatedTabs.splice(presentTabIndex, 1);
      }

      set({ tabs: updatedTabs });
      set({ activeChat: null });

      return updatedTabs;
    },

    toggleActiveChat: (id) => {
      const { activeChat } = get();
      // If the id of the chat is same of active chat we'll minimise it
      // Else we'll set the active chat to currentId
      if (id === activeChat) {
        set({ activeChat: null });
      } else {
        set({ activeChat: id });
      }
    }
  }),
  cache: {
    expiryTime: 300,
    strategy: 'memory',
    tabBehavior: 'persist',
    clearOnRefresh: true,
    scope: 'global'
  }
};

export const useGlobalChatStore = createStore(GlobalChatStoreConfig);
